import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import Select from 'react-select';
import MaskedInput from 'react-text-mask';
import { Row, Col, Button, Form, Pagination } from 'react-bootstrap';
import { FaSearch, FaFileExcel, FaEraser } from 'react-icons/fa';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import DateMask from '~/Components/DateMask';
import * as constants from '~/constant';
import * as api from '~/Services/Service';
import { exportarEmExcel, formatarTimestamp } from '~/Utils/Helpers';

const RECORDS_PER_PAGE = 20;

// W-0763 — trava a altura da área de itens selecionados nos multi-selects e
// adiciona scroll interno, evitando que a lista cresça infinitamente para baixo
// conforme o usuário seleciona muitos itens (corretores/cidades/empreendimentos).
const estilosMultiSelect = {
  valueContainer: base => ({
    ...base,
    maxHeight: 140,
    overflowY: 'auto',
  }),
};

// Formata número no padrão #,##0,00 (pt-BR, sem prefixo de moeda) — RN-10 (Valor/Vlbol).
const formatarValor = valor => {
  const numero = Number(valor);
  if (valor === null || valor === undefined || isNaN(numero)) {
    return '0,00';
  }
  return numero.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Primeiro dia do mês corrente / hoje (RN-12). DateMask espera initialValue no formato
// servidor (YYYY-MM-DDTHH:mm:ss — DATE_FORMAT_SERVER); ele renderiza dd/mm no input e, via
// onValidate, devolve a string nesse mesmo formato. Esse valor já vai direto pra WebAPI
// (DateTime binda sem ambiguidade); o Service força hora ini=00:00:00 / fim=23:59:59.
const datasIniciais = () => {
  const pad = n => String(n).padStart(2, '0');
  const hoje = new Date();
  const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  const iso = d =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00:00`;
  return { inicial: iso(primeiroDia), final: iso(hoje) };
};

const PropostaRapida = () => {
  const [gruposEmpresa, setGruposEmpresa] = useState([]);
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [corretores, setCorretores] = useState([]);

  const [geSelecionado, setGeSelecionado] = useState(null);
  const [empreendimentosSel, setEmpreendimentosSel] = useState([]);
  const [cidadesSel, setCidadesSel] = useState([]);
  const [corretoresSel, setCorretoresSel] = useState([]);

  const [carregando, setCarregando] = useState(false);
  const [carregandoCascata, setCarregandoCascata] = useState(false);
  const [resultados, setResultados] = useState([]);
  const [consultou, setConsultou] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const datasDefault = useMemo(() => datasIniciais(), []);

  // Carrega combo de GE no mount (só GEs com int_vendas — RN-01).
  useEffect(() => {
    let ativo = true;
    const carregarGes = async () => {
      try {
        const resposta = await api.listarGruposEmpresaPropostaRapida();
        if (ativo && resposta) {
          setGruposEmpresa(resposta);
        }
      } catch (error) {
        if (ativo) {
          toast.error(error && error.message ? error.message : 'Erro ao carregar GEs.');
        }
      }
    };
    carregarGes();
    return () => {
      ativo = false;
    };
  }, []);

  // Cascata (RN-13): ao escolher GE, recarrega empreendimentos/cidades/corretores.
  const carregarCascata = useCallback(async ge => {
    try {
      setCarregandoCascata(true);
      const [emp, cid, cor] = await Promise.all([
        api.listarEmpreendimentosPropostaRapida(ge),
        api.listarCidadesPropostaRapida(ge),
        api.listarCorretoresPropostaRapida(ge),
      ]);
      setEmpreendimentos(emp || []);
      setCidades(cid || []);
      setCorretores(cor || []);
    } catch (error) {
      toast.error(error && error.message ? error.message : 'Erro ao carregar filtros do GE.');
      setEmpreendimentos([]);
      setCidades([]);
      setCorretores([]);
    } finally {
      setCarregandoCascata(false);
    }
  }, []);

  const onChangeGe = opcao => {
    setGeSelecionado(opcao);
    // RN-13: ao limpar/trocar GE, limpa seleções dependentes.
    setEmpreendimentosSel([]);
    setCidadesSel([]);
    setCorretoresSel([]);
    setEmpreendimentos([]);
    setCidades([]);
    setCorretores([]);
    setResultados([]);
    setConsultou(false);
    if (opcao && opcao.value) {
      carregarCascata(opcao.value);
    }
  };

  const opcoesGe = useMemo(
    () =>
      gruposEmpresa.map(g => ({
        value: g.codigo_grupo_empresa,
        label: `${g.codigo_grupo_empresa} - ${g.apelido}`,
      })),
    [gruposEmpresa]
  );

  const opcoesEmpreendimentos = useMemo(
    () =>
      empreendimentos.map(e => ({
        value: e.codigo_familia,
        label: `${e.codigo_familia} - ${e.nome_da_familia}`,
      })),
    [empreendimentos]
  );

  const opcoesCidades = useMemo(
    () =>
      cidades.map(c => ({
        value: c.codigo_cidade,
        label: `${c.codigo_cidade} - ${c.descricao_cidade}`,
      })),
    [cidades]
  );

  const opcoesCorretores = useMemo(
    () =>
      corretores.map(c => ({
        value: c.codigo_conta,
        label: `${c.codigo_conta} - ${c.apelido_conta}`,
      })),
    [corretores]
  );

  const selecionarTodosEmpreendimentos = () => {
    setEmpreendimentosSel(opcoesEmpreendimentos);
  };

  const selecionarTodasCidades = () => {
    setCidadesSel(opcoesCidades);
  };

  const selecionarTodosCorretores = () => {
    setCorretoresSel(opcoesCorretores);
  };

  const sortedResultados = useMemo(() => {
    if (!sortConfig.key) return resultados;
    const copia = [...resultados];
    copia.sort((a, b) => {
      const av = a[sortConfig.key];
      const bv = b[sortConfig.key];
      if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
      if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return copia;
  }, [resultados, sortConfig]);

  const totalPages = Math.ceil(sortedResultados.length / RECORDS_PER_PAGE);

  const currentRecords = useMemo(() => {
    const fim = currentPage * RECORDS_PER_PAGE;
    const inicio = fim - RECORDS_PER_PAGE;
    return sortedResultados.slice(inicio, fim);
  }, [sortedResultados, currentPage]);

  // Offset absoluto para a key da linha (evita key duplicada ao paginar/ordenar — lição W-0697).
  const offsetAbsoluto = (currentPage - 1) * RECORDS_PER_PAGE;

  const ordenar = coluna => {
    let direction = 'asc';
    if (sortConfig.key === coluna && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: coluna, direction });
  };

  const trocarPagina = numero => {
    setCurrentPage(numero);
  };

  const consultar = useCallback(
    async values => {
      if (!geSelecionado || !geSelecionado.value) {
        toast.warn('GE inválido!');
        return;
      }
      // DateMask já entrega values.data_* em DATE_FORMAT_SERVER (YYYY-MM-DDTHH:mm:ss).
      const isoInicial = values.data_inicial;
      const isoFinal = values.data_final;
      if (!isoInicial || !isoFinal) {
        toast.warn('Período inválido: informe data inicial e final.');
        return;
      }
      if (empreendimentosSel.length === 0) {
        toast.warn('Nenhum Empreendimento selecionado');
        return;
      }
      if (cidadesSel.length === 0) {
        toast.warn('Nenhuma Cidade selecionada');
        return;
      }
      if (corretoresSel.length === 0) {
        toast.warn('Nenhum Corretor selecionado');
        return;
      }

      const body = {
        codigo_grupo_empresa: geSelecionado.value,
        data_inicial: isoInicial,
        data_final: isoFinal,
        tipo_data: Number(values.tipo_data),
        vinculo: Number(values.vinculo),
        status_cancelamento: Number(values.status_cancelamento),
        incluir_gravadas: values.incluir_gravadas,
        familias: empreendimentosSel.map(o => o.value),
        cidades: cidadesSel.map(o => o.value),
        corretores: corretoresSel.map(o => o.value),
      };

      try {
        setCarregando(true);
        const resposta = await api.consultarPropostaRapida(body);
        setResultados(resposta || []);
        setConsultou(true);
        setCurrentPage(1);
        setSortConfig({ key: null, direction: 'asc' });
      } catch (error) {
        toast.error(error && error.message ? error.message : 'Erro ao gerar o relatório.');
      } finally {
        setCarregando(false);
      }
    },
    [geSelecionado, empreendimentosSel, cidadesSel, corretoresSel]
  );

  const exportar = () => {
    if (!resultados.length) {
      return;
    }
    const cabecalho = [
      'Proposta',
      'Seq',
      'Qtd',
      'Valor',
      'Vlbol',
      'Família',
      'Quadra',
      'Lote',
      'Prevenda',
    ];
    const linhas = resultados.map(item => [
      item.codigo_proposta,
      item.seq,
      item.qtd,
      formatarValor(item.valor),
      formatarValor(item.vlbol),
      item.familia,
      item.quadra,
      item.lote,
      item.prevenda,
    ]);
    exportarEmExcel(
      [cabecalho, ...linhas],
      `proposta-rapida_${formatarTimestamp(new Date())}.xlsx`
    );
  };

  const podeGerar =
    !!geSelecionado &&
    empreendimentosSel.length > 0 &&
    cidadesSel.length > 0 &&
    corretoresSel.length > 0 &&
    !carregando &&
    !carregandoCascata;

  return (
    <div>
      <Header title="Proposta Rápida"></Header>
      <Formik
        initialValues={{
          data_inicial: datasDefault.inicial,
          data_final: datasDefault.final,
          tipo_data: 0,
          vinculo: 0,
          status_cancelamento: 0,
          incluir_gravadas: false,
        }}
        onSubmit={values => consultar(values)}
      >
        {({ handleSubmit, setFieldValue, values, errors, touched }) => (
          <Form className="container-fluid bg-white p-3" onSubmit={handleSubmit}>
            <Row>
              <Col xs={12} sm={6} md={4} lg={4}>
                <Form.Group controlId="grupo_empresa">
                  <Form.Label>
                    Grupo Empreendimento <span className="text-danger">*</span>
                  </Form.Label>
                  <Select
                    noOptionsMessage={() => 'Nenhum GE encontrado'}
                    placeholder="Selecione o GE..."
                    isClearable
                    options={opcoesGe}
                    value={geSelecionado}
                    onChange={onChangeGe}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} md={4} lg={2}>
                <DateMask
                  initialValue={values.data_inicial}
                  onValidate={value => setFieldValue('data_inicial', value)}
                  restrictToSqlMinDate
                  allowPast
                >
                  {({ dateError, dateValue, setDate }) => (
                    <Form.Group controlId="data_inicial">
                      <Form.Label>
                        Data Inicial <span className="text-danger">*</span>
                      </Form.Label>
                      <MaskedInput
                        name="data_inicial"
                        type="tel"
                        guide={false}
                        className={`form-control ${((errors.data_inicial &&
                          touched.data_inicial) ||
                          dateError) &&
                          'is-invalid'}`}
                        placeholder="Ex.: 01/01/2025"
                        mask={constants.MASK_DATE}
                        value={dateValue}
                        onChange={e => setDate(e.target.value)}
                      />
                      <Form.Control.Feedback type="invalid">
                        {dateError}
                      </Form.Control.Feedback>
                    </Form.Group>
                  )}
                </DateMask>
              </Col>
              <Col xs={12} sm={6} md={4} lg={2}>
                <DateMask
                  initialValue={values.data_final}
                  onValidate={value => setFieldValue('data_final', value)}
                  restrictToSqlMinDate
                  allowPast
                >
                  {({ dateError, dateValue, setDate }) => (
                    <Form.Group controlId="data_final">
                      <Form.Label>
                        Data Final <span className="text-danger">*</span>
                      </Form.Label>
                      <MaskedInput
                        name="data_final"
                        type="tel"
                        guide={false}
                        className={`form-control ${((errors.data_final &&
                          touched.data_final) ||
                          dateError) &&
                          'is-invalid'}`}
                        placeholder="Ex.: 31/01/2025"
                        mask={constants.MASK_DATE}
                        value={dateValue}
                        onChange={e => setDate(e.target.value)}
                      />
                      <Form.Control.Feedback type="invalid">
                        {dateError}
                      </Form.Control.Feedback>
                    </Form.Group>
                  )}
                </DateMask>
              </Col>
            </Row>

            <Row className="mt-2">
              <Col xs={12} md={4}>
                <Form.Label>Tipo de Data</Form.Label>
                <div>
                  <Form.Check
                    inline
                    type="radio"
                    id="tipo_data_0"
                    name="tipo_data"
                    label="Data Proposta de venda"
                    checked={Number(values.tipo_data) === 0}
                    onChange={() => setFieldValue('tipo_data', 0)}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    id="tipo_data_1"
                    name="tipo_data"
                    label="Data Efetivação"
                    checked={Number(values.tipo_data) === 1}
                    onChange={() => setFieldValue('tipo_data', 1)}
                  />
                </div>
              </Col>
              <Col xs={12} md={4}>
                <Form.Label>Vínculo Int. Vendas</Form.Label>
                <div>
                  <Form.Check
                    inline
                    type="radio"
                    id="vinculo_0"
                    name="vinculo"
                    label="Todas"
                    checked={Number(values.vinculo) === 0}
                    onChange={() => setFieldValue('vinculo', 0)}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    id="vinculo_1"
                    name="vinculo"
                    label="Vinculadas"
                    checked={Number(values.vinculo) === 1}
                    onChange={() => setFieldValue('vinculo', 1)}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    id="vinculo_2"
                    name="vinculo"
                    label="Não Vinculadas"
                    checked={Number(values.vinculo) === 2}
                    onChange={() => setFieldValue('vinculo', 2)}
                  />
                </div>
              </Col>
              <Col xs={12} md={4}>
                <Form.Label>Status</Form.Label>
                <div>
                  <Form.Check
                    inline
                    type="radio"
                    id="status_0"
                    name="status_cancelamento"
                    label="Todas"
                    checked={Number(values.status_cancelamento) === 0}
                    onChange={() => setFieldValue('status_cancelamento', 0)}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    id="status_1"
                    name="status_cancelamento"
                    label="Canceladas"
                    checked={Number(values.status_cancelamento) === 1}
                    onChange={() => setFieldValue('status_cancelamento', 1)}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    id="status_2"
                    name="status_cancelamento"
                    label="Ativas"
                    checked={Number(values.status_cancelamento) === 2}
                    onChange={() => setFieldValue('status_cancelamento', 2)}
                  />
                </div>
              </Col>
            </Row>

            <Row className="mt-2">
              <Col xs={12}>
                <Form.Check
                  type="checkbox"
                  id="incluir_gravadas"
                  name="incluir_gravadas"
                  label="Listar Propostas de Cadastro Rápido Gravados"
                  checked={values.incluir_gravadas}
                  onChange={e => setFieldValue('incluir_gravadas', e.target.checked)}
                />
              </Col>
            </Row>

            <Row className="mt-2">
              <Col xs={12} md={4}>
                <Form.Group controlId="empreendimentos">
                  <Form.Label>
                    Empreendimentos <span className="text-danger">*</span>
                  </Form.Label>
                  <Select
                    isMulti
                    closeMenuOnSelect={false}
                    styles={estilosMultiSelect}
                    isDisabled={!geSelecionado || carregandoCascata}
                    noOptionsMessage={() => 'Nenhum empreendimento'}
                    placeholder="Selecione..."
                    options={opcoesEmpreendimentos}
                    value={empreendimentosSel}
                    onChange={selecionados => setEmpreendimentosSel(selecionados || [])}
                  />
                  <div className="d-flex justify-content-end mt-1">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      type="button"
                      className="mr-2"
                      disabled={!opcoesEmpreendimentos.length || carregandoCascata}
                      onClick={selecionarTodosEmpreendimentos}
                    >
                      Todos
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      type="button"
                      disabled={!empreendimentosSel.length || carregandoCascata}
                      onClick={() => setEmpreendimentosSel([])}
                    >
                      Limpar
                    </Button>
                  </div>
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group controlId="cidades">
                  <Form.Label>
                    Cidades <span className="text-danger">*</span>
                  </Form.Label>
                  <Select
                    isMulti
                    closeMenuOnSelect={false}
                    styles={estilosMultiSelect}
                    isDisabled={!geSelecionado || carregandoCascata}
                    noOptionsMessage={() => 'Nenhuma cidade'}
                    placeholder="Selecione..."
                    options={opcoesCidades}
                    value={cidadesSel}
                    onChange={selecionados => setCidadesSel(selecionados || [])}
                  />
                  <div className="d-flex justify-content-end mt-1">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      type="button"
                      className="mr-2"
                      disabled={!opcoesCidades.length || carregandoCascata}
                      onClick={selecionarTodasCidades}
                    >
                      Todos
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      type="button"
                      disabled={!cidadesSel.length || carregandoCascata}
                      onClick={() => setCidadesSel([])}
                    >
                      Limpar
                    </Button>
                  </div>
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group controlId="corretores">
                  <Form.Label>
                    Corretores <span className="text-danger">*</span>
                  </Form.Label>
                  <Select
                    isMulti
                    closeMenuOnSelect={false}
                    styles={estilosMultiSelect}
                    isDisabled={!geSelecionado || carregandoCascata}
                    noOptionsMessage={() => 'Nenhum corretor'}
                    placeholder="Selecione..."
                    options={opcoesCorretores}
                    value={corretoresSel}
                    onChange={selecionados => setCorretoresSel(selecionados || [])}
                  />
                  <div className="d-flex justify-content-end mt-1">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      type="button"
                      className="mr-2"
                      disabled={!opcoesCorretores.length || carregandoCascata}
                      onClick={selecionarTodosCorretores}
                    >
                      Todos
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      type="button"
                      disabled={!corretoresSel.length || carregandoCascata}
                      onClick={() => setCorretoresSel([])}
                    >
                      Limpar
                    </Button>
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end align-items-center mt-2">
              <Button
                variant="outline-secondary"
                type="button"
                className="mr-2"
                onClick={() => onChangeGe(null)}
              >
                <FaEraser />
                <span className="ml-1 d-xs-none">Limpar</span>
              </Button>
              <Button disabled={!podeGerar} variant="success" type="submit">
                <FaSearch />
                <span className="ml-1 d-xs-none"> Gerar</span>
              </Button>
            </div>
          </Form>
        )}
      </Formik>

      {(carregando || carregandoCascata) && <Loading />}

      {!carregando && consultou && resultados.length === 0 && (
        <div className="container-fluid bg-white p-3 text-center text-muted">
          Nenhuma proposta encontrada para os filtros informados.
        </div>
      )}

      {!carregando && currentRecords.length > 0 && (
        <div className="container-fluid bg-white table-responsive">
          <Table className="table table-striped table-bordered table-hover table-sm">
            <Thead>
              <Tr className="text-center">
                <Th onClick={() => ordenar('codigo_proposta')} style={{ cursor: 'pointer' }}>
                  Proposta
                </Th>
                <Th onClick={() => ordenar('seq')} style={{ cursor: 'pointer' }}>
                  Seq
                </Th>
                <Th onClick={() => ordenar('qtd')} style={{ cursor: 'pointer' }}>
                  Qtd
                </Th>
                <Th onClick={() => ordenar('valor')} style={{ cursor: 'pointer' }}>
                  Valor
                </Th>
                <Th onClick={() => ordenar('vlbol')} style={{ cursor: 'pointer' }}>
                  Vlbol
                </Th>
                <Th onClick={() => ordenar('familia')} style={{ cursor: 'pointer' }}>
                  Família
                </Th>
                <Th onClick={() => ordenar('quadra')} style={{ cursor: 'pointer' }}>
                  Quadra
                </Th>
                <Th onClick={() => ordenar('lote')} style={{ cursor: 'pointer' }}>
                  Lote
                </Th>
                <Th onClick={() => ordenar('prevenda')} style={{ cursor: 'pointer' }}>
                  Prevenda
                </Th>
              </Tr>
            </Thead>
            <Tbody className="text-center">
              {currentRecords.map((item, index) => (
                <Tr
                  key={`${offsetAbsoluto + index}-${item.codigo_proposta}-${item.seq}`}
                >
                  <Td>{item.codigo_proposta}</Td>
                  <Td>{item.seq}</Td>
                  <Td>{item.qtd}</Td>
                  <Td>{formatarValor(item.valor)}</Td>
                  <Td>{formatarValor(item.vlbol)}</Td>
                  <Td>{item.familia}</Td>
                  <Td>{item.quadra}</Td>
                  <Td>{item.lote}</Td>
                  <Td>{item.prevenda}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          <div className="d-flex justify-content-end">
            <Button variant="success" type="button" onClick={exportar}>
              <FaFileExcel />
              <span className="ml-2 d-xs-none">Salvar em EXCEL</span>
            </Button>
          </div>
          <div>
            <Pagination>
              <Pagination.First
                onClick={() => trocarPagina(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => trocarPagina(currentPage - 1)}
                disabled={currentPage === 1}
              />
              {totalPages > 10 && currentPage > 5 && (
                <Pagination.Item onClick={() => trocarPagina(1)}>1</Pagination.Item>
              )}
              {totalPages > 10 && currentPage > 5 && <Pagination.Ellipsis />}

              {[...Array(totalPages).keys()]
                .slice(
                  Math.max(0, currentPage - 6),
                  Math.min(totalPages, currentPage + 4)
                )
                .map(number => (
                  <Pagination.Item
                    key={number}
                    active={currentPage === number + 1}
                    onClick={() => trocarPagina(number + 1)}
                  >
                    {number + 1}
                  </Pagination.Item>
                ))}

              {totalPages > 10 && currentPage < totalPages - 4 && (
                <Pagination.Ellipsis />
              )}
              {totalPages > 10 && currentPage < totalPages - 4 && (
                <Pagination.Item onClick={() => trocarPagina(totalPages)}>
                  {totalPages}
                </Pagination.Item>
              )}
              <Pagination.Next
                onClick={() => trocarPagina(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => trocarPagina(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropostaRapida;
